import { collection, doc } from "firebase/firestore";
import { db } from "firebaseConfig";
import type { FirestoreDataConverter } from "firebase/firestore";
import type {
  WhatsappAssistantConfig,
  WhatsappCampaign,
  WhatsappConversation,
  WhatsappMessage,
  WhatsappNumberAccess,
  WhatsappTenantStatus
} from "../../@types/sharedSchema";

/**
 * Client-side handles for the official-WhatsApp collections.
 *
 * Only the collections below are readable from the browser. `whatsapp-connections` (which holds
 * the access token), `whatsapp-webhook-inbox` (raw inbound payloads) and `whatsapp-number-map`
 * are SERVER-ONLY and deliberately have no helper here.
 *
 * Every conversations/messages query MUST carry `where("numberKey", "==", <selected>)`, and the
 * access query MUST carry `where("memberMails", "array-contains", <me>)`. Firestore rules are
 * not filters — a list query the rule can't prove from its own constraints is rejected outright.
 */

const withId = <T>(): FirestoreDataConverter<T> => ({
  toFirestore(value: any) {
    return value;
  },
  fromFirestore(snapshot, options) {
    return { ...snapshot.data(options), id: snapshot.id } as unknown as T;
  }
});

export const whatsappConversationsCollection = collection(db, "whatsapp-conversations").withConverter(
  withId<WhatsappConversation & { id: string }>()
);

/**
 * One thread's own doc — for a surface that opens a thread by id (a campaign reply, a deep link)
 * and cannot count on it being in the inbox's first page. A `get` is proven from the doc's own
 * `numberKey`, so no query constraint to carry.
 */
export const whatsappConversationDoc = (conversationId: string) => doc(whatsappConversationsCollection, conversationId);

/**
 * A conversation id is `<numberKey>__<waId>`; the number it belongs to is the prefix. Undefined
 * for anything that could not have been minted — including an id with a `/` in it, which is not
 * a document path and makes `doc()` throw (a deep link is user input).
 */
export const numberKeyOfConversation = (conversationId: string): string | undefined => {
  if (conversationId.includes("/")) return undefined;
  const [numberKey, waId] = conversationId.split("__");
  return numberKey && waId && /^\d+$/.test(numberKey) ? numberKey : undefined;
};

export const whatsappMessagesCollection = collection(db, "whatsapp-messages").withConverter(
  withId<WhatsappMessage & { id: string }>()
);

export const whatsappTenantStatusDoc = (userId: string) =>
  doc(db, "whatsapp-tenant-status", userId).withConverter(withId<WhatsappTenantStatus>());

/** One doc per surfaced number: the membership list + a status mirror. Doc id = numberKey. */
export const whatsappNumberAccessCollection = collection(db, "whatsapp-number-access").withConverter(
  withId<WhatsappNumberAccess & { id: string }>()
);

/**
 * Campaigns: client-READ ONLY (rules 2026-09) so the list and the open campaign live-update on
 * runner progress. Every query MUST carry `where("userId","==",me)` — rules are not filters.
 * The recipients SUBCOLLECTION stays server-only (E.164 doc ids); its pages ride REST.
 */
export const whatsappCampaignsCollection = collection(db, "whatsapp-campaigns").withConverter(
  withId<WhatsappCampaign & { id: string }>()
);

export const whatsappCampaignDoc = (campaignId: string) =>
  doc(db, "whatsapp-campaigns", campaignId).withConverter(withId<WhatsappCampaign & { id: string }>());

/**
 * The smart assistant's per-number config. Doc id IS the numberKey, so this is always a `get`,
 * never a list — no query constraint to carry.
 *
 * Client-READ so the settings tab live-updates; ALL writes go through
 * `POST /whatsapp/assistant/:numberKey`. The session/transcript documents are server-only and
 * deliberately have no helper here.
 */
export const whatsappAssistantDoc = (numberKey: string) =>
  doc(db, "whatsapp-assistants", numberKey).withConverter(withId<WhatsappAssistantConfig & { id: string }>());

/** Firestore stores `Timestamp`; the shared types say `Date`. Normalise at the read boundary. */
export const toJsDate = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  const ts = value as { toDate?: () => Date };
  return typeof ts?.toDate === "function" ? ts.toDate() : undefined;
};
